import moment from "moment";

import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { ContactPoint } from "@/Common/Api/Contacts/Dto/ContactPoint";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { HealthCardHelper } from "@/Common/Helpers/HealthCareHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";

abstract class BasePerson {
    public ContactPoints: ContactPoint[];
    public Phones: PhoneNumber[];
    public Addresses: Address[];
    public Emails: Email[];
    public Relationships: LinkedContact[];
    public HealthCards: PatientIdentifier[];
    public OtherPatientIdentifiers: PatientIdentifier[];
    public DoNotEmail: boolean;

    public GetCountryLookupId(): string {
        return Address.GetCountryLookupId(this.Addresses);
    }

    public GetStateLookupId(): string {
        return Address.GetStateLookupId(this.Addresses);
    }

    public GetFormattedAddress(position: number = 0, IncludeVille: boolean = true): string {
        return Address.GetFormattedAddress(this.Addresses, position, IncludeVille);
    }

    public GetFormattedEmail(position: number = 0): string {
        return Email.GetFormattedEmail(this.Emails, position);
    }

    public GetFormattedPhone(position: number, format: string = "+{0} ({1}) {2} {3} {4}", formatExtension: string = " Ext.{Ext}"): string {
        return PhoneNumber.GetFormattedPhoneNumber(position, this.Phones, format, formatExtension);
    }

    public GetFormattedCity(position: number = 0): string {
        return Address.GetFormattedCity(this.Addresses, position);
    }

    public GetFormattedZipCode(): string {
        return Address.GetFormattedZipCode(this.Addresses);
    }

    public GetFormattedProvince(): string {
        return Address.GetFormattedProvince(this.Addresses);
    }

    public GetFormattedCountry(): string {
        return Address.GetFormattedCountry(this.Addresses);
    }

    public GetFormattedNAMExpirationDateString(format?: string): string {
        const healthCard = this.HealthCards?.firstOrDefault();
        if (!healthCard?.ExpiryDate) {
            return "";
        }

        const momentDate = moment(healthCard.ExpiryDate);

        if (format) {
            return `(${momentDate.format(format)})`;
        }

        const showAlternateFormat = HealthCardHelper.requiresAlternateDateFormat(healthCard.ProvinceLookupId);
        const dateFormat = showAlternateFormat ? "YYYY / MM / DD" : "YYYY / MM";

        return `(${momentDate.format(dateFormat)})`;
    }

    public GetNAMExpirationDate(): Date {
        return this.HealthCards != null && this.HealthCards.length > 0
            ? this.HealthCards[0].ExpiryDate
            : null;
    }

    public getPersonalCellPhones(): PhoneNumber[] {
        return this.Phones?.filter(phone =>
            phone.ContactPointTypeLookupId == cacheStore.getLookupByContactPointType(ContactPointType.Personal).LookupID
            && phone.PhoneTypeLookupId == cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID
        ) ?? [];
    }

    public getPersonalEmails(): Email[] {
        return this.Emails?.filter(email =>
            email.ContactPointTypeLookupId == cacheStore.getLookupByContactPointType(ContactPointType.Personal).LookupID
        ) ?? [];
    }

    public getRepresentativesEmails(): Email[] {
        return this.Relationships?.filter(x => x.IsRepresentative).map(p => p.Emails).flatten(p => p) ?? [];
    }

    public getRepresentativesPhones(): PhoneNumber[] {
        return this.Relationships?.filter(x => x.IsRepresentative).map(p => p.PhoneNumbers).flatten(p => p) ?? [];
    }

    public getAllPersonalContactPointIds(): string[] {
        const personalContactIds = [
            ...this.getPersonalCellPhones().map(x => x.ContactPoint_id),
            ...this.getPersonalEmails().map(x => x.ContactPoint_id)
        ];
        personalContactIds.push(...this.getPersonalContactPointIdsFromRepresentatives());

        return personalContactIds;
    }

    public getPersonalContactPointIdsFromRepresentatives(): string[] {
        return this.Relationships
            ?.filter(p => p.IsRepresentative)
            .map(p => [
                ...p.Emails
                    .filter(email => email.ContactPointTypeLookupId == cacheStore.getLookupByContactPointType(ContactPointType.Personal).LookupID)
                    .map(p => p.ContactPoint_id),
                ...p.PhoneNumbers
                    .filter(phone => phone.ContactPointTypeLookupId == cacheStore.getLookupByContactPointType(ContactPointType.Personal).LookupID && phone.PhoneTypeLookupId == cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID)
                    .map(p => p.ContactPoint_id)
            ])
            .flatten(p => p) ?? [];
    }

    public getSelectedNotificationContactPointIds(): string[] {
        const contactIds = [
            ...this.Phones.filter(p => p.isSelectedForNotification).map(p => p.ContactPoint_id),
            ...this.Emails.filter(p => p.isSelectedForNotification).map(p => p.ContactPoint_id)
        ];
        contactIds.push(...this.getRepresentativesEmails().filter(p => p.isSelectedForNotification).map(p => p.ContactPoint_id));
        contactIds.push(...this.getRepresentativesPhones().filter(p => p.isSelectedForNotification).map(p => p.ContactPoint_id));

        return contactIds;
    }
}

export { BasePerson };
